import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { EntityType, useAppSelector } from '@frontend/common';
import { MqttBroker } from '@frontend/pub-sub';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { SortingState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAccountRepository } from '../account/account-repository';
import { buildCollectionMqttTopic, resolveListQueryAccountId } from '../mqtt/entity-mqtt-topic';
import { allSelect, clear, fetch as func, listStore, select } from './list-slice';

export function useAggregatedListRepository<T>(
    fetch: (arg?: ApiQueryParams<DefaultQueryParams | string | number>) => Promise<PaginatedResponse<T>>,
    dispatch: ThunkDispatch<any, any, Action>,
    eventListener?: EntityType,
    filters?: ApiQueryParams<DefaultQueryParams | string | number>
) {
    const store = useAppSelector(useSelector, listStore);
    const {
        store: { currentAccount }
    } = useAccountRepository();
    const [id] = useState<string>(crypto.randomUUID());
    const [pageSize, changePageSize] = useState(25);
    const [page, changePage] = useState(0);
    const [sortState, changeSortState] = useState<SortingState>([]);
    const [searchValue, changeSearchValue] = useState<string>('');
    const [params, changeParams] = useState<ApiQueryParams<string | number>>({});

    const refreshList = () => {
        dispatch(
            func({
                key: id,
                params: {
                    account_id: resolveListQueryAccountId(currentAccount),
                    ...params
                },
                func: fetch
            })
        );
    };

    const onSelect = (id: string) => {
        dispatch(
            select({
                listId: id,
                objId: id
            })
        );
    };

    const onAllSelect = () => {
        dispatch(
            allSelect({
                listId: id,
                index: page,
                size: pageSize
            })
        );
    };

    useEffect(() => {
        if (eventListener == undefined) return;
        const onEvent = (message: string, data: any) => {
            refreshList();
        };
        if (!currentAccount) return;
        const topic = buildCollectionMqttTopic(currentAccount, eventListener);
        MqttBroker.getInstance().subscribe(id, topic, onEvent);
        return () => {
            MqttBroker.getInstance().unSubscribe(id, topic);
            dispatch(clear(id));
        };
    }, [currentAccount]);

    useEffect(() => {
        refreshList();
    }, [params, currentAccount]);

    const data = useMemo(() => {
        if (store[id] == undefined || store[id].results == null) return null;
        let result = JSON.parse(JSON.stringify(store[id].results)) as any[];

        //sorting
        if (sortState.length > 0) {
            result = result.sort((a, b) => {
                const aValue = a[sortState[0].id];
                const bValue = b[sortState[0].id];
                if (aValue === null || bValue === null || aValue === undefined || bValue === undefined) {
                    return 0;
                }

                // Compare values based on their types
                if (typeof aValue === 'string') {
                    return sortState[0].desc ? bValue.localeCompare(aValue as string) : aValue.localeCompare(bValue as string);
                } else if (typeof aValue === 'number') {
                    return sortState[0].desc ? bValue - aValue : aValue - bValue;
                } else if (typeof aValue === 'boolean') {
                    return sortState[0].desc ? (bValue ? 1 : 0) - (aValue ? 1 : 0) : (aValue ? 1 : 0) - (bValue ? 1 : 0);
                }
                return 0;
            });
        }

        //search filter
        if (searchValue != '') {
            result = result.filter((e) => {
                return Object.keys(e).some((key) => {
                    return String(e[key]).toLowerCase().includes(searchValue.toLowerCase());
                });
            });
        }
        //pagination
        result = result.slice(page * pageSize, (page + 1) * pageSize);

        return result as T[];
    }, [store, page, sortState, pageSize, searchValue]);

    const selected = useMemo(() => {
        if (store[id] == undefined) return [];
        return store[id].selected as T[];
    }, [store]);

    const isAllSelected = useMemo(() => {
        const startPos = page * pageSize;
        const relevantObjects = store[id].results!.slice(startPos, startPos + pageSize);
        return relevantObjects.every((obj) => store[id].selected!.includes(obj));
    }, [store, page, pageSize]);

    return {
        listId: id,
        totalCount: store[id]?.count ?? 0,
        data,
        pageSize,
        page,
        changePageSize: (value: number) => changePageSize(value),
        changePage: (value: number) => changePage(value),
        sortState,
        changeSortState,
        searchValue,
        changeSearchValue,
        params,
        changeParams,
        refreshList,
        selected,
        isAllSelected,
        onSelect,
        onAllSelect
    };
}

export default useAggregatedListRepository;
